import React, { useState } from 'react';
import { SEOHead } from '../lib/seo';
import { Mail, MessageSquare, Send } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate } from 'react-router-dom';
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { useAction } from 'convex/react';
import { api } from '@/lib/convex';
import { isValidEmail } from '@/lib/email';

export default function Contact() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [holdProgress, setHoldProgress] = useState(0);
  const [isHolding, setIsHolding] = useState(false);
  const holdTimeRef = React.useRef<number>(0);
  const animationFrameRef = React.useRef<number>();
  const HOLD_DURATION = 4000; // 4 second hold duration
  const [buttonColor, setButtonColor] = useState('bg-black');

  const sendContactFormEmail = useAction(api.email.sendContactFormNotification);
  
  // Form state
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    message: ''
  });

  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const startHold = () => {
    // Validate form before allowing hold
    if (!formState.name.trim() || !formState.email.trim() || !formState.message.trim()) {
      toast({
        title: "Missing information",
        description: "Please fill in all fields before submitting.",
        variant: "destructive"
      });
      return;
    }
    
    // Validate email
    if (!isValidEmail(formState.email)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
        variant: "destructive"
      });
      return;
    }

    setIsHolding(true);
    const startTime = performance.now();
    holdTimeRef.current = startTime;

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min((elapsed / HOLD_DURATION) * 100, 100);
      setHoldProgress(progress);

      // Update button color based on progress
      if (progress < 25) {
        setButtonColor('bg-red-600');
      } else if (progress < 50) {
        setButtonColor('bg-orange-600');
      } else if (progress < 75) {
        setButtonColor('bg-yellow-600');
      } else {
        setButtonColor('bg-green-600');
      }

      if (progress < 100) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);
  };

  const endHold = () => {
    setIsHolding(false);
    setHoldProgress(0);
    setButtonColor('bg-black');
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
  };

  const checkHoldCompletion = (e: React.FormEvent) => {
    e.preventDefault();
    const holdTime = performance.now() - holdTimeRef.current;
    
    if (holdTime >= HOLD_DURATION) {
      handleSubmit(e);
    } else {
      toast({
        title: "Hold longer",
        description: "Please hold the button until it fills completely",
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Send email via Resend
      const result = await sendContactFormEmail({
        name: formState.name,
        email: formState.email,
        message: formState.message
      });
      
      if (!result.success) {
        throw new Error(result.error || "Failed to send message");
      }
      
      // Create form data for Netlify forms backup
      const formData = new FormData(e.currentTarget);
      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as any).toString(),
      });

      setIsSubmitting(false);
      (e.target as HTMLFormElement).reset();
      navigate('/thank-you');
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      });
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead
        title="Contact Us - Get Help with AI Content Generation"
        description="Need help with AISEOBlogGenerator? Contact our support team for questions about AI content generation, subscription plans, or feature requests. We typically respond within 24 hours."
        type="website"
        canonicalUrl="https://aiseobloggenerator.com/contact"
      />
      <section className="relative py-20 bg-cream z-10">
      <div className="container px-4 mx-auto max-w-6xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">Let's Talk</h2>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto">
            Have questions about our AI Blog Writer? We're here to help! Reach out to our team for support, feature requests, or partnership inquiries.
          </p>
        </div>

        <div className="grid md:grid-cols-5 gap-8">
          <div className="md:col-span-2 space-y-8">
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-md">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-dogblue/10 rounded-full flex items-center justify-center">
                  <Mail className="w-6 h-6 text-dogblue" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1">Email Us</h3>
                  <p className="text-gray-600 mb-3">
                    For general inquiries, reach out at:
                  </p>
                  <a href="mailto:support@aiseobloggenerator.com" className="text-dogblue font-medium hover:underline">
                    support@aiseobloggenerator.com
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-md">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-dogbrown/10 rounded-full flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-dogbrown" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1">Support Hours</h3>
                  <p className="text-gray-600">
                    Our team is available Monday through Friday, 9am-5pm EST.
                  </p>
                  <div className="mt-3 text-sm text-gray-500">
                    <div className="flex justify-between border-b border-gray-100 py-1">
                      <span>Monday - Friday</span>
                      <span>9:00 AM - 5:00 PM EST</span>
                    </div>
                    <div className="flex justify-between border-b border-gray-100 py-1">
                      <span>Saturday</span>
                      <span>Closed</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span>Sunday</span>
                      <span>Closed</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <form 
            name="contact" 
            method="POST" 
            onSubmit={checkHoldCompletion}
            data-netlify="true" 
            className="relative md:col-span-3 bg-white p-6 rounded-xl shadow-lg border"
          >
            <input type="hidden" name="form-name" value="contact" />
            <input type="hidden" name="subject" value="Contact Form Submission" />
            
            <h2 className="text-xl font-bold mb-6">Send us a message</h2>
            
            <div className="space-y-5">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  className="relative z-20 bg-gray-50 border-gray-200"
                  type="text"
                  id="name"
                  name="name"
                  required
                  placeholder="Your name"
                  value={formState.name}
                  onChange={handleChange}
                />
              </div>

              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  type="text"
                  id="email"
                  name="email"
                  required
                  placeholder="your@email.com"
                  value={formState.email}
                  onChange={handleChange}
                  className="bg-gray-50 border-gray-200"
                />
              </div>

              <div>
                <Label htmlFor="message">Message</Label>
                <textarea
                  className="relative z-20 w-full min-h-[150px] p-3 border rounded-md resize-y bg-gray-50 border-gray-200 focus:outline-none focus:ring-2 focus:ring-dogblue/50"
                  id="message"
                  name="message"
                  required
                  placeholder="Your message here..."
                  value={formState.message}
                  onChange={handleChange}
                />
              </div>

              <Button 
                type="submit" 
                className={`w-full relative z-20 overflow-hidden transition-colors duration-300 ${buttonColor} ${isHolding ? 'animate-shake' : ''} text-white`}
                onMouseDown={startHold}
                onMouseUp={endHold}
                onMouseLeave={endHold}
                onTouchStart={startHold}
                onTouchEnd={endHold}
                disabled={isSubmitting}
              >
                <Send className="w-4 h-4 mr-2" />
                {isSubmitting ? 'Sending...' : 'Send Message'}
                <div
                  className="absolute inset-0 bg-white/20"
                  style={{
                    width: `${holdProgress}%`,
                    transition: 'width 0.05s linear'
                  }}
                />
                <div className="absolute bottom-0 left-0 text-[10px] text-white/70 ml-1 mb-0.5">
                  Hold to send ({Math.round((HOLD_DURATION - (holdProgress / 100 * HOLD_DURATION)) / 1000)}s)
                </div>
              </Button>
              
              <p className="text-xs text-center text-gray-500 mt-4">
                We typically respond within 24 hours during business days.
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
    </>
  );
}